/** English is the reference dictionary: its keys define the shape every other
 *  locale must satisfy (see `Messages` in ./index.ts), so a missing French
 *  key is a type error rather than a key leaking into the UI at runtime. */
export const en = {
  'app.name': 'Biller Portal',
  'app.skipToContent': 'Skip to main content',
  'app.logoAlt': 'smobilpay, by Maviance',

  'landing.title': 'Smobilpay Biller Portal',
  'landing.tagline': 'Reconcile with Smobilpay collections, export what you need',
  'landing.signIn': 'Sign in',
  'landing.signingIn': 'Redirecting to sign in…',
  'landing.mfaNote': 'Sign-in is protected by two-factor authentication.',
  'landing.sessionExpired': 'That sign-in link had expired, so we started a new one. Please sign in again — if you have just changed your password, use the new one.',
  'landing.signInFailed': 'Sign-in could not be completed. Please try again.',
  'landing.organisationDisabled': 'This organisation has been deactivated, so its accounts cannot sign in. Please contact your Maviance representative.',
  'landing.signedOut': 'You have been signed out.',

  'nav.transactions': 'Transactions',
  'nav.users': 'Users',
  'nav.billers': 'Billers',
  'nav.gateways': 'Gateways',
  'nav.profile': 'My Profile',
  'nav.primary': 'Primary',

  'shell.signOut': 'Sign out',
  'shell.signingOut': 'Signing out…',
  'shell.allBillers': 'All organisations',
  'shell.selectBiller': 'Select organisation',
  'shell.group.billers': 'Providers',
  'shell.group.beneficiaries': 'Beneficiaries',
  'shell.language': 'Language',
  'shell.timezoneNote': 'All dates shown in {timezone}',
  'shell.version': 'Version {version}',
  'tx.note.title': 'What this list shows and how to use it',
  'tx.note.scope': 'You see exactly the transactions in your organisation’s scope and nothing else: a provider sees every transaction settled through its gateways; a beneficiary sees the rows on a gateway that carry its identity. The scope comes from what the organisation holds, never from who is signed in.',
  'tx.note.dates': 'Dates are Africa/Douala business time. From and To each take a day and, optionally, a time; left at their defaults they cover whole days. A beneficiary with a settlement cut-off gets the “Settlement day” checkbox: one day as the provider settles it, from the previous day at the cut-off to the cut-off minus one second.',
  'tx.note.filters': 'Status, amount, PTN, provider transaction ID and service label (and service type, where granted) narrow the list; filters apply on Apply, and the whole view — filters, sort, page — lives in the URL, so a link reproduces it. Reset returns to the current month.',
  'tx.note.columns': 'Which columns you see is set per organisation by Maviance (Field exposure): the default fields are always there; gateway, service, receipt numbers and the like appear only when granted. Click a row for its full detail.',
  'tx.note.export': 'Export XLSX writes the transactions matching the current filters — every page, not only the one on screen — with a Metadata sheet stating the scope and the filters used.',
  'tx.note.internal': 'Internal users pick an organisation in the sidebar to see exactly what its users see, columns included, or stay on “All organisations” to search the whole platform with the default columns. Acting for a beneficiary organisation they also see, and can filter by, the beneficiary identity, which no organisation user ever sees.',

  'role.internal': 'Internal',
  'role.billerUser': 'Biller user',

  'apiError.sessionEnded': 'Your session has ended. Please sign in again.',
  'apiError.sessionChanged': 'Your session changed in another tab. Reload the page and try again.',
  'apiError.notPermitted': 'You do not have access to that.',
  'apiError.organisationDisabled': 'Your organisation has been deactivated. Please contact your Maviance representative.',
  'apiError.notFound': 'That could not be found.',
  'apiError.tooMany': 'Too many requests just now. Please wait a moment and try again.',
  'apiError.exportTooLarge': 'That export is too large. Narrow the filters and try again.',
  'apiError.temporarilyUnavailable': 'This is temporarily unavailable. Please try again shortly.',
  'apiError.unexpected': 'Something went wrong at our end. Please try again.',
  'error.title': 'Something went wrong',
  'error.body': 'The page could not be displayed. You can try again, or reload if it keeps happening.',
  'error.retry': 'Try again',
  'error.reference': 'Reference {requestId}',
  'error.notFound': 'That page does not exist.',
  'error.backToTransactions': 'Back to transactions',
  'error.loadFailed': 'Could not load this page.',

  'common.loading': 'Loading…',
  'common.close': 'Close',
  'common.dismiss': 'Dismiss',
  'common.cancel': 'Cancel',

  'tx.from': 'From',
  'tx.fromTime': 'From time',
  'tx.toTime': 'To time',
  'tx.settlementDay': 'Settlement day (cut-off {cutOff})',
  'tx.settlementDayOnly': 'Settlement day',
  'tx.settlementDayColumn': 'Settlement day',
  'tx.settlementDayDetail': '{day} ({cutOff} cut-off)',
  'tx.settlementDayHint': 'One day, as the provider settles it: from the previous day at {cutOff} to {cutOff} minus one second. Untick for a range with times.',
  'tx.to': 'To',
  'tx.status': 'Status',
  'tx.status.SUCCESS': 'Success',
  'tx.status.FAILED': 'Failed',
  'tx.status.PENDING': 'Pending',
  'tx.status.REVERSED': 'Reversed',
  'tx.amountMin': 'Amount from',
  'tx.amountMax': 'Amount to',
  'tx.ptn': 'PTN',
  'tx.gateway.all': 'All gateways',
  'tx.beneficiary': 'Beneficiary',
  'tx.beneficiaryHolder': 'Held by',
  'tx.beneficiary.unassigned': 'Unassigned',
  'tx.billNumber': 'Bill number',
  'tx.svcNmb': 'Service number',
  'tx.cusNmb': 'Customer number',
  'tx.providerTxid': 'Provider transaction ID',
  'tx.collectorCompanyBid': 'Partner ID',
  'tx.cdata': 'Customer data (CDATA)',
  'tx.beneficiary.hint': 'Any part of the recorded beneficiary identity, case-insensitive.',
  'tx.biller': 'Biller',
  'tx.biller.all': 'All organisations',
  'tx.biller.unassigned': 'Unassigned',
  'tx.pageSize': 'Rows',
  'tx.apply': 'Apply',
  'tx.reset': 'Reset',
  'tx.date': 'Date',
  'tx.amount': 'Amount',
  'tx.gateway': 'Gateway',
  'tx.service': 'Service',
  'tx.serviceLabel': 'Service Label',
  'tx.serviceLabel.all': 'All service labels',
  'tx.notAvailable': 'n/a',
  'tx.serviceType': 'Service type',
  'tx.serviceType.all': 'All service types',
  'tx.payerReference': 'Payer reference',
  'tx.note': 'Remittance details',
  'tx.detailTitle': 'Transaction detail',
  'tx.caption': '{count} transactions, dates in {timezone}',
  'tx.loading': 'Loading transactions',
  'tx.export': 'Export XLSX',
  'tx.exporting': 'Preparing export…',
  'tx.exportDone': 'Downloaded {filename}',
  'tx.emptyTitle': 'No transactions yet',
  'tx.emptyBody': 'Transactions appear here once collections start flowing through Smobilpay.',
  'tx.emptyFilteredTitle': 'No transactions match these filters',
  'tx.emptyFilteredBody': 'Try widening the date range, or clearing the status and amount filters.',
  'tx.summaryFiltered': 'Shown',
  'tx.summaryValue': 'Value',
  'tx.summarySuccess': 'Successful',
  'tx.summaryFailed': 'Failed',
  'tx.summaryOfTotal': 'of {total} in range',
  'tx.summaryLoading': 'Loading summary',
  'tx.paginationRegion': 'Transaction pages',
  'tx.previous': 'Previous',
  'tx.next': 'Next',
  'tx.pageSummary': 'Page {page} of {pages} · {records} transactions',
  'tx.sortAsc': 'Sort by {column}, ascending',
  'tx.sortDesc': 'Sort by {column}, descending',

  'users.name': 'Name',
  'users.email': 'E-mail',
  'users.status': 'Status',
  'users.mfa': 'MFA',
  'users.mfaOn': 'Enrolled',
  'users.mfaPending': 'Pending',
  'users.created': 'Created',
  'users.actions': 'Actions',
  'users.active': 'Active',
  'users.disabled': 'Disabled',
  'users.edit': 'Edit',
  'users.deactivate': 'Deactivate',
  'users.reactivate': 'Reactivate',
  'users.resetPassword': 'Reset password',
  'users.newUser': 'New user',
  'users.createTitle': 'Invite a user',
  'users.editTitle': 'Edit user',
  'users.createAndInvite': 'Create & send invitation',
  'users.save': 'Save',
  'users.firstName': 'First name',
  'users.lastName': 'Last name',
  'users.emailImmutable': 'The e-mail address identifies the account and cannot be changed.',
  'users.emailInvalid': 'Enter a valid e-mail address.',
  'users.required': 'This field is required.',
  'users.caption': '{count} users',
  'users.scope': 'Users for {biller}',
  'users.loading': 'Loading users',
  'users.emptyTitle': 'No users yet',
  'users.emptyBody': 'Create a user to give them access to this biller.',
  'users.internalNote.title': 'Creating or removing an internal user',
  'users.internalNote.why': 'This screen manages one biller’s users. Internal accounts are not created here on purpose: an internal user can read every biller’s payment data, so the portal does not let one internal account create another.',
  'users.internalNote.who': 'These steps need a Keycloak realm administrator.',
  'users.internalNote.step1': 'Open the Keycloak admin console — the server the portal sends you to for the password and code screens — and select the “biller-portal” realm.',
  'users.internalNote.step2': 'Users → Add user. Fill in Username, Email, First name and Last name, and switch “Email verified” on. Create.',
  'users.internalNote.step3': 'Credentials → Credential reset: add the reset action “Update Password”, then Send email. They get an email with a link — valid for 12 hours — and choose their own password; you never see it.',
  'users.internalNote.step4': 'Role mapping → Assign role → filter by realm roles → tick “internal”. Assign. Do this before their first sign-in — without the role the portal refuses them with a generic “sign-in failed”, and keeps refusing until the role exists.',
  'users.internalNote.step5': 'Leave the “biller_id” attribute empty. It is what limits a biller user to one organisation, and an internal user must not have one.',
  'users.internalNote.step6': 'Done. They enrol an authenticator themselves at first sign-in — two-factor is enforced by the realm, not by you.',
  'users.internalNote.caution': 'Never give an account both the “internal” role and a “biller_id”. Internal wins, so the account would silently see every biller rather than the one you intended.',
  'users.internalNote.deactivate': 'To remove access, set the account to Disabled in Keycloak rather than deleting it — the audit trail still refers to it. A live session lasts until its token expires, up to 15 minutes; to cut access at once, also sign out their sessions on the account’s Sessions tab.',
  'users.pickBillerTitle': 'Select a biller first',
  'users.pickBillerBody': 'Users belong to an organisation. Choose one from “Select organisation” in the sidebar to see and manage its users.',
  'users.forbiddenTitle': 'User accounts are managed by Maviance',
  'users.forbiddenBody': 'Contact your Maviance representative to add, change or remove a user for your organisation.',
  'users.invited': 'Invitation sent to {email}',
  'users.updated': '{name} updated',
  'users.deactivated': '{name} deactivated',
  'users.reactivated': '{name} reactivated',
  'users.resetSent': 'Password reset sent to {email}',
  'users.cannotActOnSelf': 'You cannot deactivate your own account.',

  'profile.scopeTitle': 'Your access',
  'profile.securityTitle': 'Security',
  'profile.name': 'Name',
  'profile.email': 'E-mail',
  'profile.roles': 'Roles',
  'profile.organisation': 'Organisation',
  'profile.biller': 'Biller',
  'profile.allBillers': 'All organisations',
  'profile.gateways': 'Gateways',
  'profile.changePassword': 'Change password',
  'profile.changePasswordHint': 'You will be asked to confirm your current password.',
  'profile.reconfigureMfa': 'Re-configure authenticator',
  'profile.reconfigureMfaHint': 'Replaces the authenticator app on your account.',

  'billers.title': 'Billers',
  'billers.subtitle': 'Every organisation that reconciles collections through this portal — providers with their gateways, beneficiaries with their slices — and the people who administer them.',
  'billers.note.title': 'What an organisation is here, and what this screen does',
  'billers.note.what': 'An organisation is a company whose people sign in to reconcile collections. Its users see exactly the transactions in its scope and nothing else; the scope comes from what the organisation holds, never from the user.',
  'billers.note.provider': 'A provider is a biller in the usual sense: the company Smobilpay delivers payments to. It holds gateways — the connections its payments settle through — and sees every transaction on them. Gateways are assigned on the Gateways screen.',
  'billers.note.beneficiary': 'A beneficiary is a company paid under a provider’s gateway — an insurer whose certificates a customs switch collects on its behalf, for instance. It holds slices: on a gateway, the identity its payments carry. It sees only those rows, never the whole gateway, and at most one identity per gateway. Identities are never typed in — they exist once Core has recorded them on payments; a slice is assigned among them, from the organisation’s own form here or from the gateway’s Beneficiaries panel.',
  'billers.note.here': 'On this screen you add an organisation and invite its first user, rename it, set a beneficiary’s settlement cut-off (which gives its users the “Settlement day” filter), manage a beneficiary’s slices, and deactivate or reactivate it — deactivation signs its users out at once.',
  'billers.note.users': 'Only Maviance creates portal users; an organisation’s people cannot add colleagues. The Users screen adds, resets and deactivates them, and the Field exposure screen decides which optional fields each organisation may see.',
  'billers.note.fixed': 'The kind cannot be changed afterwards and an organisation cannot be deleted: its assignments are the record of who could see what. Deactivate instead.',
  'billers.add': 'Add biller',
  'billers.code': 'Biller ID',
  'billers.name': 'Name',
  'billers.created': 'Created',
  'billers.users': 'Users',
  'billers.status': 'Status',
  'billers.gateways': 'Gateways',
  'billers.active': 'Active',
  'billers.inactive': 'Deactivated',
  'billers.activate': 'Activate',
  'billers.deactivate': 'Deactivate',
  'billers.edit': 'Edit',
  'billers.empty': 'No billers yet',
  'billers.emptyBody': 'Add the first organisation to start assigning gateways to it.',
  'billers.new.title': 'Add a biller',
  'billers.new.codeHint': 'Short, permanent identifier — letters, digits, hyphen and underscore.',
  'billers.new.firstUserEmail': 'First user e-mail',
  'billers.new.firstUserEmailHint': 'A portal account is created for this address and an invitation is sent to it. They can read this biller\u2019s transactions; only Maviance can add more people to it.',
  'billers.new.firstName': 'First name',
  'billers.new.lastName': 'Last name',
  'billers.new.submit': 'Create and invite',
  'billers.edit.title': 'Edit {name}',
  'billers.edit.submit': 'Save changes',
  'billers.gatewaysHint': 'Only transactions on the assigned gateways are shown to this biller’s users.',
  'billers.gatewaysAssigned': 'Assigned: {names}',
  'billers.gatewaysNoneAssigned': 'No gateways assigned yet.',
  'billers.gatewaysMore': 'and {count} more',
  'billers.manageGateways': 'Assign or detach on the Gateways screen',
  'billers.gatewaysAfterCreate': 'Assign gateways after creating, on the Gateways screen.',
  'billers.kind': 'Kind',
  'billers.kind.provider': 'Provider',
  'billers.kind.beneficiary': 'Beneficiary',
  'billers.kindHint': 'A provider holds gateways and sees every transaction on them. A beneficiary holds slices of a gateway — its identity on that gateway — and sees only those transactions. The kind cannot be changed afterwards.',
  'billers.settlementCutOff': 'Settlement cut-off (HH:MM)',
  'billers.settlementCutOffHint': 'Optional, beneficiaries only. When set, the transaction list offers a “Settlement day” checkbox: one day, running from the previous day at this time to this time minus one second — the day as the provider reports it.',
  'billers.slices': 'Slices',
  'billers.slicesHint': 'A slice is a gateway together with a beneficiary identity recorded on its payments.',
  'billers.slicesHeld': '{count} slice(s)',
  'billers.slicesAfterCreate': 'Slices are assigned right after creating, in this form.',
  'billers.slices.none': 'No slices yet — this organisation sees nothing until one is assigned.',
  'billers.slices.gateway': 'Gateway',
  'billers.slices.pickGateway': 'Choose a gateway',
  'billers.slices.identity': 'Beneficiary identity',
  'billers.slices.identityHint': 'The identities Core has recorded on that gateway’s payments and that no organisation holds yet. Identities are never typed in — they appear once payments carry them.',
  'billers.slices.pickIdentity': 'Choose an identity',
  'billers.slices.noIdentities': 'No unassigned identity recorded on this gateway yet',
  'billers.slices.assignHint': 'Assign a slice: choose the gateway, then the identity this organisation is paid under on it — it is assigned as soon as you choose it.',
  'billers.slices.revoke': 'Revoke',
  'billers.slices.assigned': '{identity} on {gateway} assigned.',
  'billers.slices.revoked': '{identity} on {gateway} revoked.',
  'billers.created.toast': '{name} created and an invitation was sent to {email}.',
  'billers.updated.toast': '{name} updated.',
  'billers.activated.toast': '{name} reactivated.',
  'billers.deactivated.toast': '{name} deactivated — its users lost access immediately.',
  'billers.deactivate.confirm': 'Deactivate {name}? Every one of its users loses access to the portal immediately.',

  'gateways.title': 'Gateways',
  'gateways.subtitle': 'Every gateway Smobilpay has settled a transaction through. Label each one so people can recognise it, and assign it to its biller — assignment decides which transactions that biller’s users can see.',
  'gateways.search': 'Search name or ID',
  'gateways.beneficiaries': 'Beneficiaries',
  'gateways.beneficiaries.action': 'Beneficiaries ({count} identities, {unassigned} unassigned)',
  'gateways.beneficiaries.lead': 'Every identity Core recorded on this gateway’s payments. Identities are never typed in — they appear here once payments carry them. “Assigned to” is the organisation whose users see those rows; an identity nobody holds is visible to the gateway’s provider and to internal users only. Revoking an assignment does not remove the identity — it is in the data — and the identity can be assigned again.',
  'gateways.beneficiaries.title': 'Beneficiaries on {gateway}',
  'gateways.beneficiaries.identity': 'Identity',
  'gateways.beneficiaries.name': 'Name',
  'gateways.beneficiaries.count': 'Transactions',
  'gateways.beneficiaries.none': '(no beneficiary) · {count} row(s)',
  'gateways.beneficiaries.assigned': '{identity} assigned to {organisation}.',
  'gateways.beneficiaries.revoked': '{identity} is no longer assigned.',
  'gateways.unmappedOnly': 'Only unmapped',
  'gateways.biller': 'Biller',
  'gateways.allBillers': 'All organisations',
  'gateways.id': 'Gateway ID',
  'gateways.coreName': 'Core name',
  'gateways.label': 'Label',
  'gateways.display': 'Shown as',
  'gateways.assignedTo': 'Assigned to',
  'gateways.unassigned': 'Unassigned',
  'gateways.unmapped': 'Unmapped',
  'gateways.labelPlaceholder': 'Give it a name',
  'gateways.save': 'Save',
  'gateways.saved': '{name} saved.',
  'gateways.assigned': '{gateway} assigned to {biller}.',
  'gateways.detached': '{gateway} is no longer assigned to any biller.',
  'gateways.empty': 'No gateways match',
  'gateways.emptyBody': 'Nothing here matches the current filters. Clear them to see the whole catalogue.',
  'gateways.emptyAll': 'No gateways yet',
  'gateways.emptyAllBody': 'A gateway appears here once Smobilpay has settled a transaction through it.',
  'gateways.count': '{shown} of {total} gateways',
  'gateways.note.title': 'What a gateway is, and why this mapping matters',
  'gateways.note.what': 'A gateway is the technical connection Smobilpay uses to deliver a payment to a provider’s system — one configured connector with its endpoint and credentials. Every settled transaction records the gateway it went out through; that is how the rows in this portal get their gateway id.',
  'gateways.note.whatNot': 'It is not the channel the customer paid from (agent, app or partner API), and it does not set fees or settlement — it is the exit route on the provider side.',
  'gateways.note.why': 'The payment platform never records which biller a gateway belongs to: one connection can serve many services, and an aggregator connection can carry several billers’ payments. This screen is the only place that relationship exists, which is why it is maintained by hand.',
  'gateways.note.assign': 'Assigning a gateway decides visibility: a biller’s users see exactly the transactions settled through the gateways assigned to them. Until a gateway is assigned, its transactions are visible to internal staff only — a newly onboarded biller sees nothing until you assign theirs.',
  'gateways.note.caution': 'Assign a gateway only when every payment it carries belongs to that biller. Assignment shares all of the gateway’s transactions, so an aggregator connection serving several billers must stay unassigned until it can be attributed cleanly.',
  'gateways.note.beneficiaries': 'The Beneficiaries panel lists the identities Core recorded on the gateway’s payments, whoever holds them. An identity is assigned to a beneficiary organisation from that panel or from the organisation’s own form on the Billers screen — never typed in. Revoking an assignment only returns the identity to the unassigned list; it never removes it.',
  'gateways.note.label': 'The label is the name everyone sees, shown as “Label (GW-1234)” in transaction lists, filters, exports and the biller’s own profile. Without one, people are left with the raw id.',

  'tx.fee': 'Fee',
  'tx.collectedTotal': 'Total collected',
  'tx.paidAt': 'Paid at',
  'tx.statusUpdatedAt': 'Status changed at',
  'tx.sourceStatus': 'Core status',
  'nav.fieldExposure': 'Field exposure',
  'nav.support': 'Support',

  // ── Support (biller users) ──────────────────────────────────────────────
  'support.title': 'Support',
  'support.subtitle': 'Send a request to Maviance support. Your name, e-mail address and organisation travel with it, and replies go to your e-mail.',
  'support.internalTitle': 'The support form is for biller users',
  'support.internalBody': 'Internal staff have Maviance’s own channels — this form sends to the inbox that answers billers.',
  'support.topic': 'Topic',
  'support.topicHint': 'Helps the support team route your request.',
  'support.topic.ACCESS': 'Account & access',
  'support.topic.TRANSACTIONS': 'Transactions & reconciliation',
  'support.topic.EXPORTS': 'Exports & reports',
  'support.topic.USERS': 'User management',
  'support.topic.OTHER': 'Other',
  'support.message': 'Message',
  'support.messageHint': 'Describe the problem — what you did, what you expected, and what happened instead. PTNs and dates help a lot.',
  // MPAY-32085 — the field holds an over-long message rather than trimming it,
  // so it has to say where the limit falls and why Send is refused.
  'support.charsRemaining': '{count} characters remaining',
  'support.charsOver': '{count} characters too many',
  'support.messageTooLong': 'Your message is too long to send. Shorten it to {max} characters or fewer.',
  'support.messageRequired': 'A message is required.',
  'support.attachments': 'Attachments',
  'support.addAttachment': 'Add attachment',
  'support.attachmentHint': 'Up to 3 files, 2 MB each — screenshots (PNG, JPEG, GIF, WebP), PDF, TXT, CSV or XLSX.',
  'support.removeAttachment': 'Remove',
  'support.tooManyFiles': 'At most {max} attachments are allowed.',
  'support.fileTooLarge': '{name} exceeds the 2 MB limit.',
  'support.fileEmpty': '{name} is empty.',
  'support.badFormat': '{name}: this file format is not accepted — use images, PDF, TXT, CSV or XLSX.',
  'support.send': 'Send request',
  'support.sent': 'Sent. The support team will reply to your e-mail address.',
  'support.sendFailed': 'The request could not be sent. Please try again, and use your usual Maviance contact if it persists.',
  // ── Field exposure (TRD 8002) ───────────────────────────────────────────
  'fieldExposure.title': 'Field exposure',
  'fieldExposure.subtitle': 'Which extra fields each biller may see. The fields shown on every transaction list are not listed here — they are always visible and nothing here can withdraw them.',
  'fieldExposure.defaultsNote.before': 'Every biller already sees',
  'fieldExposure.defaultsNote.fields': 'PTN|date and time|service label|amount|currency|status|service number|provider transaction ID',
  'fieldExposure.defaultsNote.and': 'and',
  'fieldExposure.defaultsNote.after': '. This screen only adds to that.',
  'fieldExposure.readOnlyNote': 'Viewing the whole policy, read-only. Select a biller in the sidebar to grant or revoke fields for it — every grant is for exactly one biller.',
  'fieldExposure.note.title': 'What field exposure is, and what a grant changes',
  'fieldExposure.note.what': 'Every biller’s users see the same base transaction row — the default fields listed below. Some Core fields carry more than the biller’s own business: the raw remittance document names third parties, and the fee is Smobilpay’s commercial margin. Those are withheld from every biller until someone grants them here.',
  'fieldExposure.note.tiers': 'Three tiers, and this screen can touch only one: the default fields are always visible and nothing here can withdraw them; the fields on this screen are withheld until granted; platform internals are not listed and cannot be granted by anyone.',
  'fieldExposure.note.impact': 'A grant takes effect immediately: the field appears in that biller’s transaction lists, detail view and exports on their next request — there is no release step. Revoking withholds it again just as immediately.',
  'fieldExposure.note.how': 'Every grant is for exactly one biller: select the biller in the sidebar, then Grant on the field’s card, optionally narrowed to one service type. A field that every biller should see is not a grant — it belongs in the platform defaults.',
  'fieldExposure.note.caution': 'Sensitive fields disclose third-party personal data or Smobilpay’s commercial margin. Every grant and revoke is recorded permanently — who, when, and the reason given — and that record is what a compliance review reads.',
  'fieldExposure.forbiddenTitle': 'Field exposure is managed by Maviance',
  'fieldExposure.forbiddenBody': 'Which fields your organisation can see is configured by Maviance Operations. Contact your Maviance representative if you need a field that is not shown.',
  'fieldExposure.loading': 'Loading the field exposure policy',
  'fieldExposure.loadFailed': 'The field exposure policy could not be loaded.',
  'fieldExposure.sensitive': 'Sensitive',
  'fieldExposure.withheld': 'Not granted',
  'fieldExposure.grantCount': '{count} grant(s)',
  'fieldExposure.grant': 'Grant',
  'fieldExposure.coversAll': 'Granted for every service type — nothing more to grant; revoke to narrow it.',
  'fieldExposure.pickOrganisation': 'Select an organisation in the sidebar to grant this field.',
  'fieldExposure.grantTitle': 'Grant {field}',
  'fieldExposure.granted': 'Granted. Billers will see the field on their next request.',
  'fieldExposure.revoke': 'Revoke',
  'fieldExposure.revoked': 'Revoked. The field is withheld again immediately.',
  'fieldExposure.revokeFailed': 'The grant could not be revoked.',
  'fieldExposure.tableCaption': 'Grants for {field}',
  'fieldExposure.biller': 'Biller',
  'fieldExposure.anyBiller': 'Every biller',
  'fieldExposure.serviceType': 'Service type',
  'fieldExposure.serviceTypeHint': 'Restrict the grant to one service type, as it appears on the transaction list.',
  'fieldExposure.anyServiceType': 'Any service type',
  'fieldExposure.any': 'Any',
  'fieldExposure.scopedNoteBefore': 'Showing the grants that apply to',
  'fieldExposure.scopedNoteAfter': '— its own and those made for every organisation. Switch to “All organisations” to see the whole policy.',
  'fieldExposure.billerFixedHint': 'the biller selected in the sidebar. To grant this field for another biller, select that biller first.',
  'fieldExposure.reason': 'Reason',
  'fieldExposure.reasonHint': 'Why this grant exists — a ruling reference, a ticket, a decision. Recorded permanently against your name.',
  'fieldExposure.reasonRequired': 'A reason is required. This is what justifies the grant in a compliance review.',
  'fieldExposure.grantedBy': 'Granted by',
  'fieldExposure.sensitiveWarning': 'This field discloses more than the biller’s own business — third-party personal data, or Smobilpay’s commercial margin. Grant it only with a decision you can point to.',
  'fieldExposure.everyoneWarning': 'Granted to EVERY biller on EVERY transaction — a legacy platform-wide rule. If that is intended, it belongs in the default tier, not here.',
} as const

export type MessageKey = keyof typeof en
